import axiosInstance from '../RestClient';

// Business units (SHOW_ATTRIBUTION): accounts flagged as own legal entities,
// the business units ("unidades de negocio", stored as centers) each record is
// attributed to — optionally with a partner (socio) whose clients' money is
// liquidated to them — and the per-entity / per-partner report. See
// routes/attributionRoutes.js — every endpoint answers 404 while the flag is off.
const BASE_URI = '/attribution';

class AttributionService {
	/** → { entities: [{ _id, name, type, rut, currency }] } */
	async listEntities() {
		const response = await axiosInstance.get(`${BASE_URI}/entities`);
		return response;
	}

	/** Admin. → { message, account: { _id, name, isOwnEntity } } */
	async setOwnEntity(accountId, isOwnEntity) {
		const response = await axiosInstance.put(`${BASE_URI}/entities/${accountId}`, { isOwnEntity });
		return response;
	}

	/** → { centers: [{ id, name, active, observations, entity: { id, name } | null, partner: { id, name } | null }] } */
	async listCenters({ entity, all } = {}) {
		const params = {};
		if (entity) params.entity = entity;
		if (all) params.all = 1;
		const response = await axiosInstance.get(`${BASE_URI}/centers`, { params });
		return response;
	}

	/** Admin. payload: { name, entityId?, partnerId?, observations? } → { message, center } */
	async createCenter(payload) {
		const response = await axiosInstance.post(`${BASE_URI}/centers`, payload);
		return response;
	}

	/** Admin. payload: { name?, entityId?, partnerId? (null clears), active?, observations? } → { message, center } */
	async updateCenter(centerId, payload) {
		const response = await axiosInstance.put(`${BASE_URI}/centers/${centerId}`, payload);
		return response;
	}

	/**
	 * Admin. payload: exactly one of contractId | creditId | billId | billTemplateId | assetId
	 * (the asset's unit is the default of its contracts, their liquidations and its bills),
	 * plus centerId?: id | null and entityId?: id | null (entityId only with creditId).
	 * → { message, kind, id, center, entity }
	 */
	async assign(payload) {
		const response = await axiosInstance.put(`${BASE_URI}/assign`, payload);
		return response;
	}

	/**
	 * `from` may be empty (whole history).
	 * → { from, to, entities: [{ id, name, totals, centers: [{ id|null, name ('General' when unassigned), totals }] }],
	 *     unattributed: { dues, creditDues },
	 *     partners: [{ unit, entity, partner, summary, balance, costs, credit, rows }],
	 *     sharedCosts: [{ entity, totals: { UYU?: { total, socios: { name: amount }, empresa } } }] }
	 * Everything money-related is keyed by currency ({ USD?: {...}, UYU?: {...} }) and never summed together.
	 * See services/attribution.js partnerSettlement for the partner fields.
	 */
	async getReport({ from, to, entity } = {}) {
		const params = { to };
		if (from) params.from = from;
		if (entity) params.entity = entity;
		const response = await axiosInstance.get(`${BASE_URI}/report`, { params });
		return response;
	}
}

export default new AttributionService();
